import React, { useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { Toast } from '@/components/Toast'
import { useAuth } from '@/hooks/useAuth'
import {
  StudyService,
  UserProfileHelper,
  DEVICE_COLORS,
  getRandomDeviceColor
} from '@lura/shared'
import type { StudyWithCounts, StudyWithAssignment } from '@lura/shared'

export const StudyListPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [studies, setStudies] = useState<
    (StudyWithCounts | StudyWithAssignment)[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<
    'alphabetical' | 'date-newest' | 'date-oldest'
  >('alphabetical')

  // Create study form state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newStudyTitle, setNewStudyTitle] = useState('')
  const [newStudyDescription, setNewStudyDescription] = useState('')
  const [newStudyColor, setNewStudyColor] = useState(getRandomDeviceColor())
  const [creating, setCreating] = useState(false)

  // Access check - Study Admins and clinicians only
  if (!UserProfileHelper.isStudyAdmin(user) && !UserProfileHelper.isClinician(user)) {
    return <Navigate to="/studies" replace />
  }

  const isStudyAdmin = UserProfileHelper.isStudyAdmin(user)

  const fetchStudies = async () => {
    try {
      setLoading(true)
      setError(null)
      // Admins see all studies, clinicians see only their assigned studies
      if (isStudyAdmin) {
        const data = await StudyService.getAllStudies()
        setStudies(data)
      } else if (user?.profile?.id) {
        const data = await StudyService.getClinicianStudies(user.profile.id)
        setStudies(data)
      } else {
        setStudies([])
      }
    } catch (err) {
      console.error('Error fetching studies:', err)
      setError(err instanceof Error ? err.message : 'Failed to load studies')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStudies()
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleCreateStudy = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newStudyTitle.trim()) {
      setErrorMessage('Study title is required')
      return
    }

    if (!user?.profile?.id) {
      setErrorMessage('User profile not available')
      return
    }

    try {
      setCreating(true)
      setErrorMessage(null)
      setSuccessMessage(null)
      await StudyService.createStudy(
        newStudyTitle.trim(),
        newStudyDescription.trim() || undefined,
        user.profile.id,
        newStudyColor
      )

      // Reset form
      setNewStudyTitle('')
      setNewStudyDescription('')
      setNewStudyColor(getRandomDeviceColor())
      setShowCreateForm(false)
      setSuccessMessage('Study created successfully')

      // Refresh studies list
      await fetchStudies()
    } catch (err) {
      console.error('Error creating study:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to create study'
      )
    } finally {
      setCreating(false)
    }
  }

  // Filter and sort studies
  const filteredAndSortedStudies = React.useMemo(() => {
    let filtered = studies

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = studies.filter(
        study =>
          study.title.toLowerCase().includes(searchLower) ||
          study.description?.toLowerCase().includes(searchLower)
      )
    }

    // Apply sorting
    const sorted = [...filtered]
    if (sortBy === 'alphabetical') {
      sorted.sort((a, b) => a.title.localeCompare(b.title))
    } else if (sortBy === 'date-newest') {
      sorted.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } else if (sortBy === 'date-oldest') {
      sorted.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    }

    return sorted
  }, [studies, searchQuery, sortBy])

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Studies"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              Studies
            </h2>
            {isStudyAdmin && (
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
              >
                {showCreateForm ? 'Cancel' : '+ Create Study'}
              </button>
            )}
          </div>

          {/* Search and Sort */}
          <div className="mb-6 space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search studies..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-2.5 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Sort Buttons */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Sort by:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortBy('alphabetical')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'alphabetical'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Alphabetical
                </button>
                <button
                  onClick={() => setSortBy('date-newest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-newest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Newest First
                </button>
                <button
                  onClick={() => setSortBy('date-oldest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-oldest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Oldest First
                </button>
              </div>
            </div>
          </div>

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Create Study Form - Admin Only */}
          {isStudyAdmin && showCreateForm && (
            <div className="mb-6 bg-white shadow-sm rounded-3xl overflow-hidden p-6">
              <h3 className="text-xl font-medium text-dark-blue mb-4">
                Create New Study
              </h3>
              <form onSubmit={handleCreateStudy}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Study Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={newStudyTitle}
                      onChange={e => setNewStudyTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                      placeholder="Enter study title"
                      disabled={creating}
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={newStudyDescription}
                      onChange={e => setNewStudyDescription(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                      placeholder="Enter study description (optional)"
                      disabled={creating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Study Color
                    </label>
                    <div className="space-y-1">
                      {/* Light colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(0, 17).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setNewStudyColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              newStudyColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={creating}
                            title={color}
                          />
                        ))}
                      </div>
                      {/* Medium colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(17, 34).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setNewStudyColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              newStudyColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={creating}
                            title={color}
                          />
                        ))}
                      </div>
                      {/* Dark colors */}
                      <div className="flex gap-1">
                        {DEVICE_COLORS.slice(34).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setNewStudyColor(color)}
                            className={`w-8 h-8 rounded-md border-2 transition-all flex-shrink-0 ${
                              newStudyColor === color
                                ? 'border-dark-blue ring-2 ring-dark-blue ring-offset-1'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={creating}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Selected color: {newStudyColor}
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false)
                        setNewStudyTitle('')
                        setNewStudyDescription('')
                        setNewStudyColor(getRandomDeviceColor())
                        setError(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-3xl text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
                      disabled={creating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-3xl text-sm font-medium text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors disabled:opacity-50"
                      disabled={creating}
                    >
                      {creating ? 'Creating...' : 'Create Study'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Studies Table */}
          {!loading && !error && (
            <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {isStudyAdmin ? 'All Studies' : 'My Studies'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {isStudyAdmin
                    ? 'All studies in the system'
                    : 'Studies assigned to you'}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-highlight-green">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Study Title
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Created Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Clinicians
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Devices
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedStudies.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-12 text-center text-sm text-gray-500"
                        >
                          {searchQuery
                            ? 'No studies found matching your search'
                            : isStudyAdmin
                              ? 'No studies found. Create your first study to get started.'
                              : 'You are not assigned to any studies yet.'}
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedStudies.map(study => (
                        <tr
                          key={study.id}
                          onClick={() => navigate(`/studies/${study.id}`)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <ColorSquare color={study.color} size="md" />
                              <div className="text-sm font-medium text-gray-900">
                                {study.title}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500 max-w-md truncate">
                              {study.description || '—'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(study.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                              {study.clinician_count}{' '}
                              {study.clinician_count === 1
                                ? 'Clinician'
                                : 'Clinicians'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {study.device_count}{' '}
                              {study.device_count === 1 ? 'Device' : 'Devices'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex justify-end">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
